/**
 * Which payer the sponsor tab should have selected, given the patient's payer
 * list and whatever is currently selected.
 *
 * Flutter parity — `payment_widget.dart:151-195`. Flutter indexes the list with
 *
 *   (appointment.sponsor != null && appointment.sponsorType == <this type>)
 *     ? list.indexWhere((e) => e.provider.id == appointment.sponsor.id &&
 *                              e.policyNumber == appointment.policyNo)
 *     : 0
 *
 * i.e. KEEP the appointment's own payer when it belongs to the list being
 * shown, and only fall back to the first entry otherwise.
 *
 * Two deliberate differences from that Dart:
 *
 *  1. Flutter's `indexWhere` returns -1 on no match and then indexes `list[-1]`,
 *     which throws. Here a failed match falls back to the first entry, which is
 *     what the `: 0` branch would have done anyway.
 *  2. The match is expressed over `(id, policyNumber)` pairs rather than
 *     positions, so it is stable if the backend ever reorders the list.
 *
 * Matching on BOTH id and policy number is not incidental: one payer can appear
 * twice for a patient under different policies, and the panel's card and chip
 * highlight already key off the same pair. A looser match here would select an
 * entry the UI then fails to highlight.
 */

/** Minimal shape shared by health insurances and corporate agreements. */
export interface SponsorOption {
  policyNumber?: string | null;
  providerId?: string | null;
}

/**
 * @param options  the payer list for the ACTIVE sponsor tab, in wire order
 * @param currentId  `sponsorId` currently on the form (seeded from the saved
 *                   appointment on open, or set by the user's last click)
 * @param currentPolicy  `policyNo` currently on the form
 * @returns the option to select, or `null` when the list is empty
 */
export function pickSponsorSelection<T extends SponsorOption>(
  options: readonly T[],
  currentId: string | null | undefined,
  currentPolicy: string | null | undefined,
): T | null {
  if (options.length === 0) return null;

  const keep = options.find(
    (o) =>
      !!o.providerId &&
      o.providerId === currentId &&
      (o.policyNumber ?? '') === (currentPolicy ?? ''),
  );

  return keep ?? options[0] ?? null;
}
